import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import { getDataUserId } from "../../util/manageAuth";
import { ChatSearchParams, ChatSearchResponse, MessageSearchParams, MessageSearchResponse } from "../../types/messageType";
import { Broadcast, BroadcastRecipientsResponse, BroadcastSearchParams, BroadcastSearchResponse } from "../../types/broadcastType";

const safeUrl = (url?: string) => {
    if (!url) return "";
    try {
        return encodeURI(decodeURI(url));
    } catch {
        return encodeURI(url);
    }
};

export const fetchMessages = createAsyncThunk<
    MessageSearchResponse,
    MessageSearchParams | undefined,
    { rejectValue: string }
>(
    "messages/fetchMessages",
    async (params, thunkAPI) => {
        try {
            const userId = getDataUserId();
            if (!userId) throw new Error("User not authenticated");

            const page = params?.page ?? 0;
            const size = params?.size ?? 10;

            const body = {
                userId,
                toNumber: params?.filters?.toNumber || "",
                templateName: params?.filters?.templateName || "",
                status: params?.filters?.status || "",
                deliveryStatus: params?.filters?.deliveryStatus || "",
            };

            const response = await customFetch.post(
                `/api/whatsapp/send/whatsapp/messages/search?page=${page}&size=${size}`,
                body
            );

            let payload = response.data;
            if (typeof payload?.data === "string") {
                payload.data = JSON.parse(payload.data);
            }

            return {
                results: payload.data.results,
                page: payload.data.page,
            };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.message || "Failed to fetch messages"
            );
        }
    }
);

export const fetchChatMessages = createAsyncThunk<
    ChatSearchResponse,
    ChatSearchParams,
    { rejectValue: string }
>(
    "messages/fetchChatMessages",
    async (params, thunkAPI) => {
        try {
            const userId = getDataUserId();
            if (!userId) throw new Error("User not authenticated");

            const page = params?.page ?? 0;
            const size = params?.size ?? 10;

            const response = await customFetch.get(
                `/api/whatsapp/send/whatsapp/conversations/search`,
                {
                    params: {
                        userId,
                        mobile: params.mobile,
                        direction: params.direction || "",
                        page,
                        size
                    }
                }
            );

            let payload = response.data;

            if (typeof payload?.data === "string") {
                payload.data = JSON.parse(payload.data);
            }

            return {
                results: payload.data.results,
                page: payload.data.page
            };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.message || "Failed to fetch chat messages"
            );
        }
    }
);

// Cache so each media file is downloaded only once per session
const mediaCache = new Map<string, Promise<string>>();
export const getWhatsappMediaSrc = (mediaId: string): Promise<string> => {
    if (!mediaCache.has(mediaId)) {
        const request = (async () => {
            const userId = getDataUserId();
            if (!userId) throw new Error("User not authenticated");

            const response = await customFetch.get(
                `/api/whatsapp/media/${encodeURIComponent(mediaId)}`,
                {
                    params: { userId },
                    responseType: "blob",
                }
            );

            const blob: Blob = response.data;

            // API returned JSON (e.g. { data: { url } }) instead of the file
            if (blob.type.includes("json")) {
                const json = JSON.parse(await blob.text());
                let data = json?.data ?? json;
                if (typeof data === "string") {
                    try {
                        data = JSON.parse(data);
                    } catch {
                        // plain URL string
                    }
                }
                const url = typeof data === "string" ? data : data?.url || data?.mediaUrl;
                if (!url) throw new Error("Media URL not found");
                return url as string;
            }

            return URL.createObjectURL(blob);
        })();

        // Don't cache failures, so it can be retried
        request.catch(() => mediaCache.delete(mediaId));
        mediaCache.set(mediaId, request);
    }

    return mediaCache.get(mediaId)!;
};

export type WhatsappMessageType = "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT";
export interface SendWhatsappMessageParams {
    mobile: string;
    message: string;
    type?: WhatsappMessageType;
    mediaId?: string;
    mediaUrl?: string;
}

export const sendWhatsappMessage = createAsyncThunk<
    any,
    SendWhatsappMessageParams,
    { rejectValue: string }
>(
    "messages/sendWhatsappMessage",
    async (params, thunkAPI) => {
        try {
            const userId = getDataUserId();
            if (!userId) throw new Error("User not authenticated");

            const body = {
                userId: Number(userId),
                mobile: params.mobile,
                message: params.message,
                mediaId: safeUrl(params.mediaId),
                mediaUrl: safeUrl(params.mediaUrl),
                type: params.type || "TEXT",
            };

           const response = await customFetch.post(`/whatsapp`, body);

            let payload = response.data;

            if (typeof payload?.data === "string") {
                try {
                    payload.data = JSON.parse(payload.data);
                } catch {
                    // plain string response — keep as is
                }
            }

            // Some APIs return 200 with success=false
            if (payload?.success === false) {
                throw new Error(payload?.message || "Failed to send message");
            }

            return payload?.data ?? payload;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to send message"
            );
        }
    }
);

export const fetchBroadcasts = createAsyncThunk<
  BroadcastSearchResponse,
  BroadcastSearchParams | undefined,
  { rejectValue: string }
>("broadcasts/fetchBroadcasts", async (params, thunkAPI) => {
  try {
    const page = params?.page ?? 0;
    const size = params?.size ?? 20;

    const response = await customFetch.get(`/api/broadcasts`, {
      params: {
        page,
        size,
      },
    });

    const payload = response.data;

    if (payload?.status !== 1) {
      throw new Error(payload?.error || "Failed to fetch broadcasts");
    }

    return {
      results: payload?.data?.results ?? [],
      page: payload?.data?.page ?? {
        index: page,
        size,
        total: 0,
        count: 0,
      },
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch broadcasts",
    );
  }
});

export const fetchBroadcastById = createAsyncThunk<
  Broadcast,
  number,
  { rejectValue: string }
>("broadcasts/fetchBroadcastById", async (id, thunkAPI) => {
  try {
    const response = await customFetch.get(`/api/broadcasts/${id}`);
    const payload = response.data;
    if (payload?.status !== 1) {
      throw new Error(payload?.error || "Failed to fetch broadcast details");
    }
    if (!payload?.data) {
      throw new Error("Broadcast details not found");
    }
    return payload.data as Broadcast;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch broadcast details",
    );
  }
});

export const fetchBroadcastRecipients = createAsyncThunk<
  BroadcastRecipientsResponse,
  { broadcastId: number; page: number; size: number },
  { rejectValue: string }
>(
  "broadcasts/fetchBroadcastRecipients",
  async ({ broadcastId, page, size }, thunkAPI) => {
    try {
      const response = await customFetch.get(
        `/api/broadcasts/${broadcastId}/recipients`,
        { params: { page, size } },
      );
      const payload = response.data;
      if (payload?.status !== 1) {
        throw new Error(
          payload?.error || "Failed to fetch broadcast recipients",
        );
      }
      if (!payload?.data) {
        throw new Error("Recipient data not found");
      }
      return payload.data as BroadcastRecipientsResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch broadcast recipients",
      );
    }
  },
);